import {
  DropDownList,
  type DropDownListProps,
} from '@progress/kendo-react-dropdowns'
import classNames from 'classnames'
import React from 'react'

import { Typography } from '~/components/elements/Typography'

import styles from './ItemSelector.css'

export type ItemSelectorProps = Pick<
  DropDownListProps,
  'className' | 'dataItemKey' | 'disabled' | 'onBlur' | 'value'
> & {
  'data-testid'?: string
  data: NonNullable<DropDownListProps['data']> | null
  onChange?: (value: NonNullable<DropDownListProps['value']>) => void
  textField: NonNullable<DropDownListProps['textField']>
  width?: 'default' | 160 | 240 | 360
}

// FIXME: @progress/kendo-react-dropdowns@9 に上げた影響でRefの型が変わり、コメント時点のコードでは挙動への影響は無いものの、型の修正の影響範囲が広いため暫定対応としてanyにしています。
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ItemSelectorRef = any

export const ItemSelector = React.forwardRef<
  ItemSelectorRef,
  ItemSelectorProps
>(
  (
    {
      className,
      data,
      'data-testid': dataTestId = 'components-elements-item-selector',
      onChange,
      textField,
      width,
      ...rest
    },
    ref,
  ) => {
    const itemRender = React.useMemo<DropDownListProps['itemRender']>(
      () => (li, itemProps) => {
        const text = itemProps.dataItem[textField]

        return React.cloneElement(
          li,
          li.props,
          <Typography color="inherit" variant="normal">
            {text}
          </Typography>,
        )
      },
      [textField],
    )

    const valueRender = React.useMemo<DropDownListProps['valueRender']>(
      () => (span, item) => {
        const text = item?.[textField]

        return React.cloneElement(
          span,
          span.props,
          <Typography variant="normal">{text}</Typography>,
        )
      },
      [textField],
    )

    const handleChange = React.useCallback<EH<DropDownListProps, 'onChange'>>(
      (event) => {
        onChange?.(event.value)
      },
      [onChange],
    )

    return (
      <DropDownList
        ref={ref}
        className={classNames(
          styles.root,
          {
            [styles[`width-${width}`]]: width !== 'default',
          },
          className,
        )}
        data={data ?? undefined}
        data-testid={dataTestId}
        itemRender={itemRender}
        textField={textField}
        valueRender={valueRender}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
